// Provides a generic dashboard system for use in LibreQoS and Insight
import {DashboardLayout} from "./layout";
import {resetWS, subscribeWS} from "./ws";
import {heading5Icon} from "../helpers/content_builders";
import {openDashboardEditor} from "./dashboard_editor";

export class Dashboard {
    // Takes the target DIV in which to build the dashboard,
    // and the name of the dashboard for cookie and builder
    // purposes
    constructor(divName, cookieName, defaultLayout, widgetFactory, dashletMenu, hasCadence = true, savedDashUrl = "/local-api/dashletThemes", useWebSocket = true) {
        this.divName = divName;
        this.cookieName = cookieName;
        window.cookieName = cookieName;
        this.widgetFactory = widgetFactory;
        this.dashletMenu = dashletMenu;
        this.savedDashUrl = savedDashUrl;
        this.useWebSocket = useWebSocket;

        // Set up the div
        this.parentDiv = document.getElementById(this.divName);
        if (this.parentDiv === null) {
            throw new Error("No element found with the id '" + this.divName + "'");
        }

        // Editor Support
        this.#editButton(hasCadence);
        if (localStorage.getItem("forceEditMode")) {
            localStorage.removeItem("forceEditMode");
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.editMode();
                })
            });
        }

        // Content
        this.dashlets = [];
        this.tabDashlets = {}; // dashlets organized by tab
        this.layout = new DashboardLayout(cookieName, defaultLayout);
        this.dashletIdentities = this.layout.dashlets;

        // Data Wrangling
        this.channels = [];

        // Auto Refresh Handling
        this.paused = false;
        let cadence = localStorage.getItem("dashCadence");
        if (cadence === null) {
            this.cadence = 1;
            localStorage.setItem("dashCadence", this.cadence.toString());
        } else {
            this.cadence = parseInt(cadence);
        }
        this.tickCounter = 0;
    }


    build() {
        this.dispose();
        this.#filterWidgetList();
        this.#buildTabUI();
        this.#buildTabContents();
        this.#buildChannelList(this.dashlets);
        if (this.useWebSocket) {
            this.#webSocketSubscription();
        } else {
            for (let i=0; i<this.dashlets.length; i++) {
                this.#safeSetupOnce(this.dashlets[i], {});
            }
        }
    }

    #buildTabUI() {
        // Create tab navigation
        let tabNav = document.createElement('ul');
        tabNav.classList.add('nav', 'nav-tabs', 'mb-3');
        tabNav.id = this.divName + '_tabs';
        
        this.layout.tabs.forEach((tab, index) => {
            let li = document.createElement('li');
            li.classList.add('nav-item');
            
            let a = document.createElement('a');
            a.classList.add('nav-link');
            if (index === this.layout.activeTab) {
                a.classList.add('active');
            }
            a.href = '#';
            a.innerText = tab.name;
            a.onclick = (e) => {
                e.preventDefault();
                this.switchTab(index);
            };
            
            li.appendChild(a);
            tabNav.appendChild(li);
        });
        
        this.parentDiv.appendChild(tabNav);
    }

    #buildTabContents() {
        // Create tab content container
        let tabContent = document.createElement('div');
        tabContent.classList.add('tab-content');
        tabContent.id = this.divName + '_tab_content';
        
        this.childIds = [];
        
        this.layout.tabs.forEach((tab, tabIndex) => {
            let tabPane = document.createElement('div');
            tabPane.classList.add('tab-pane', 'row');
            tabPane.id = this.divName + '_tab_' + tabIndex;
            
            if (tabIndex === this.layout.activeTab) {
                tabPane.classList.add('active');
                tabPane.style.display = 'flex';
            } else {
                tabPane.style.display = 'none';
            }
            
            // Build dashlets for this tab
            let tabDashlets = this.tabDashlets[tabIndex] || [];
            tabDashlets.forEach(dashlet => {
                let div = dashlet.buildContainer();
                this.childIds.push(div.id);
                tabPane.appendChild(div);
            });
            
            tabContent.appendChild(tabPane);
        });
        
        this.parentDiv.appendChild(tabContent);
    }

    switchTab(tabIndex) {
        if (tabIndex === this.layout.activeTab) return;
        
        // Update active tab in layout
        this.layout.activeTab = tabIndex;
        this.layout.save(this.layout);
        
        // Update tab navigation
        let tabs = document.querySelectorAll('#' + this.divName + '_tabs .nav-link');
        tabs.forEach((tab, index) => {
            if (index === tabIndex) {
                tab.classList.add('active');
            } else {
                tab.classList.remove('active');
            }
        });
        
        // Update tab content visibility
        let tabPanes = document.querySelectorAll('#' + this.divName + '_tab_content .tab-pane');
        tabPanes.forEach((pane, index) => {
            if (index === tabIndex) {
                pane.classList.add('active');
                pane.style.display = 'flex';
                // Notify dashlets in this tab that they're now visible
                let tabDashlets = this.tabDashlets[index] || [];
                tabDashlets.forEach(dashlet => {
                    if (dashlet.onTabActivated) {
                        dashlet.onTabActivated();
                    }
                });
                
                // Resize all ECharts instances in the newly visible tab
                setTimeout(() => {
                    // Give DOM time to update, then resize all charts
                    const charts = pane.querySelectorAll('.dashgraph, .dashgraphZoomed');
                    charts.forEach(chartDiv => {
                        if (typeof echarts !== 'undefined') {
                            const chart = echarts.getInstanceByDom(chartDiv);
                            if (chart) {
                                chart.resize();
                            }
                        }
                    });
                }, 0);
            } else {
                pane.classList.remove('active');
                pane.style.display = 'none';
            }
        });
    }

    #filterWidgetList() {
        this.dashlets = [];
        this.tabDashlets = {};
        let globalIndex = 0;
        
        // Process each tab
        this.layout.tabs.forEach((tab, tabIndex) => {
            this.tabDashlets[tabIndex] = [];
            
            tab.dashlets.forEach((dashletDef) => {
                let widget = this.widgetFactory(dashletDef.tag, globalIndex);
                if (widget == null) return; // Skip build

                widget.size = dashletDef.size;
                widget.tabIndex = tabIndex;
                widget.tag = dashletDef.tag;
                
                this.dashlets.push(widget);
                this.tabDashlets[tabIndex].push(widget);
                globalIndex++;
            });
        });
    }


    #clearRenderedDashboard() {
        while (this.parentDiv.children.length > 0) {
            this.parentDiv.removeChild(this.parentDiv.lastChild);
        }
    }

    dispose() {
        resetWS();
        if (Array.isArray(this.dashlets)) {
            this.dashlets.forEach((dashlet) => {
                if (dashlet && typeof dashlet.dispose === "function") {
                    dashlet.dispose();
                }
            });
        }
        this.dashlets = [];
        this.tabDashlets = {};
        this.channels = [];
        this.childIds = [];
        this.#clearRenderedDashboard();
    }

    #buildChannelList() {
        this.channels = [];
        for (let i=0; i<this.dashlets.length; i++) {
            let channels = this.dashlets[i].subscribeTo();
            for (let j=0; j<channels.length; j++) {
                if (!this.#alreadySubscribed(channels[j])) {
                    this.channels.push(channels[j]);
                }
            }
        }
    }

    #alreadySubscribed(name) {
        for (let i=0; i<this.channels.length; i++) {
            if (this.channels[i] === name) {
                return true;
            }
        }
        return false;
    }

    #webSocketSubscription() {
        if (this.channels.length === 0) {
            // We're not in WS mode
            for (let i=0; i<this.dashlets.length; i++) {
                this.#safeSetupOnce(this.dashlets[i], {});
            }
            return;
        }
        subscribeWS(this.channels, (msg) => {
            if (msg.event === "join") {
                // The DOM will be present now, setup events
                for (let i=0; i<this.dashlets.length; i++) {
                    this.#safeSetupOnce(this.dashlets[i], msg);
                }
            } else {
                // Propagate the message
                if (!this.paused) {
                    this.tickCounter++;
                    this.tickCounter %= this.cadence;
                    for (let i = 0; i < this.dashlets.length; i++) {
                        if (this.dashlets[i].canBeSlowedDown()) {
                            if (this.tickCounter === 0) {
                                this.#safeOnMessage(this.dashlets[i], msg);
                            }
                        } else {
                            this.#safeOnMessage(this.dashlets[i], msg);
                        }
                    }
                }
            }
        });
    }

    #safeSetupOnce(dashlet, msg) {
        try {
            dashlet.setupOnce(msg);
        } catch (error) {
            let name = "unknown";
            try {
                name = dashlet?.title?.() || dashlet?.constructor?.name || "unknown";
            } catch (e) {
                name = dashlet?.constructor?.name || "unknown";
            }
            console.error(`Dashlet setup failed: ${name}`, error);
        }
    }

    dispatchMessage(msg) {
        for (let i = 0; i < this.dashlets.length; i++) {
            this.#safeOnMessage(this.dashlets[i], msg);
        }
    }

    #safeOnMessage(dashlet, msg) {
        try {
            dashlet.onMessage(msg);
        } catch (error) {
            if (dashlet.__dispatchErrorLogged) {
                return;
            }
            dashlet.__dispatchErrorLogged = true;
            let name = "unknown";
            try {
                name = dashlet?.title?.() || dashlet?.constructor?.name || "unknown";
            } catch (e) {
                name = dashlet?.constructor?.name || "unknown";
            }
            console.error(`Dashlet update failed: ${name}`, error);
        }
    }

    #editButton(hasCadence) {
        let parent = document.getElementById("controls");
        if (parent === null) return;
        let editDiv = document.createElement("span");
        editDiv.id = this.divName + "_edit";
        editDiv.innerHTML = "<button type='button' class='btn btn-outline-secondary btn-sm' id='btnEditDash'><i class='fa fa-pencil'></i> Edit</button>";
        editDiv.onclick = () => {
            // New Editor - pass the full layout with tabs
            let availableElements = [];
            this.dashletMenu.forEach((d) => {
                let category = "Uncategorized";
                if (d.category !== null) category = d.category;
                availableElements.push({
                    name: d.name,
                    size: d.size,
                    tag: d.tag,
                    category,
                });
            });

            openDashboardEditor(this.layout, availableElements, (newLayout) => {
                console.log("New dashboard layout:", newLayout);
                this.layout.save(newLayout);
                window.location.reload();
            }, this.cookieName);
        };

        // Cadence Picker
        let cadenceDiv = document.createElement("div");
        cadenceDiv.id = this.divName + "_cadence";
        let cadenceLabel = document.createElement("label");
        cadenceLabel.htmlFor = "cadencePicker";
        cadenceLabel.innerText = "Refresh Rate: ";
        let cadencePicker = document.createElement("input");
        cadencePicker.id = "cadencePicker";
        cadencePicker.type = "number";
        cadencePicker.min = "1";
        cadencePicker.max = "60";
        cadencePicker.value = this.cadence;
        cadencePicker.onchange = () => {
            this.cadence = parseInt(cadencePicker.value);
            localStorage.setItem("dashCadence", this.cadence.toString());
        }
        cadenceDiv.appendChild(cadenceLabel);
        cadenceDiv.appendChild(cadencePicker);

        // Pause Button
        let pauseDiv = document.createElement("span");
        pauseDiv.id = this.divName + "_pause";
        pauseDiv.innerHTML = "<button type='button' class='btn btn-secondary btn-sm ms-2'><i class='fa fa-pause'></i> Pause</button>";
        pauseDiv.onclick = () => {
            this.paused = !this.paused;
            let target = document.getElementById(this.divName + "_pause");
            if (this.paused) {
                target.innerHTML = "<button type='button' class='btn btn-secondary btn-sm'><i class='fa fa-play'></i> Resume</button>";
            } else {
                target.innerHTML = "<button type='button' class='btn btn-secondary btn-sm'><i class='fa fa-pause'></i> Pause</button>";
            }
        };

        parent.appendChild(editDiv);
        if (hasCadence) {
            parent.appendChild(pauseDiv);
            parent.appendChild(cadenceDiv);
        }
    }

    editMode() {
        // Insert a Temporary Div to hold edit options
        let editDiv = document.createElement("div");
        editDiv.classList.add("col-12", "bg-secondary-subtle", "mb-2");
        editDiv.id = "dashboardEditingDiv";
        editDiv.style.padding = "10px";
        editDiv.style.borderRadius = "5px";
        editDiv.style.marginLeft = "0";
        let toasts = document.getElementById("toasts");
        this.editingDashboard = true;

        // Add the editing elements
        let row = document.createElement("div");
        row.classList.add("row");

        let c1 = document.createElement("div");
        c1.classList.add("col-3");
        c1.appendChild(heading5Icon("gear", "Dashboard Options"));
        let nuke = document.createElement("button");
        nuke.type = "button";
        nuke.classList.add("btn", "btn-sm", "btn-secondary");
        nuke.innerHTML = "<i class='fa fa-trash'></i> Remove All";
        nuke.onclick = () => { this.removeAll(); };
        c1.appendChild(nuke);
        let filler = document.createElement("button");
        filler.type = "button";
        filler.classList.add("btn", "btn-sm", "btn-secondary");
        filler.innerHTML = "<i class='fa fa-plus-square'></i> Add All";
        filler.onclick = () => { this.addAll(); };
        filler.style.marginLeft = "5px";
        c1.appendChild(filler);

        let c2 = document.createElement("div");
        c2.classList.add("col-3");
        c2.appendChild(heading5Icon("plus", "Add Dashboard Item"));
        let list = document.createElement("div");
        list.classList.add("dropdown");
        list.id = "dropdown-widgets";
        let listBtn = document.createElement("button");
        listBtn.type = "button";
        listBtn.classList.add("btn", "btn-secondary", "btn-sm", "dropdown-toggle");
        listBtn.setAttribute("data-bs-toggle", "dropdown");
        listBtn.innerHTML = "<i class='fa fa-plus'></i> Add Widget";
        list.appendChild(listBtn);
        let listUl = document.createElement("ul");
        listUl.classList.add("dropdown-menu", "dropdown-menu-sized");
        this.dashletMenu.forEach((d) => {
            let entry = document.createElement("li");
            let item = document.createElement("a");
            item.classList.add("dropdown-item");
            item.innerText = d.name;
            let myTag = d.tag;
            item.onclick = () => {
                let didSomething = false;
                this.dashletMenu.forEach((d) => {
                    if (d.tag === myTag) {
                        this.dashletIdentities.push(d);
                        didSomething = true;
                    }
                });
                if (didSomething) {
                    this.#replaceDashletList();
                }
            };
            entry.appendChild(item);
            listUl.appendChild(entry);
        });
        list.appendChild(listUl);
        c2.appendChild(list);

        let c3 = document.createElement("div");
        c3.classList.add("col-3");
        c3.appendChild(heading5Icon("save", "Save Layout"));

        let saveGroup = document.createElement("div");
        saveGroup.classList.add("input-group");
        let saveAppend = document.createElement("div");
        saveAppend.classList.add("input-group-append");

        let lbl = document.createElement("label");
        lbl.htmlFor = "saveDashName";
        let saveDashName = document.createElement("input");
        saveDashName.id = "saveDashName";
        saveDashName.type = "text";
        saveDashName.classList.add("form-control", "border-0", "small");
        let saveBtn = document.createElement("button");
        saveBtn.type = "button";
        saveBtn.classList.add("btn", "btn-secondary", "btn-sm");
        saveBtn.innerHTML = "<i class='fa fa-save'></i>";
        saveBtn.style.marginLeft = "4px";
        saveBtn.onclick = () => {
            let name = $("#saveDashName").val();
            if (name.length < 1) return;
            let request = {
                name: name,
                entries: this.dashletIdentities
            }
            $.ajax({
                type: "POST",
                url: "/local-api/dashletSave",
                data: JSON.stringify(request),
                contentType : 'application/json',
                success: () => {
                    localStorage.setItem("forceEditMode", "true");
                    window.location.reload();
                }
            })
        }
        saveGroup.appendChild(saveDashName);
        saveAppend.appendChild(saveBtn);
        saveGroup.appendChild(saveAppend);
        c3.appendChild(saveGroup);

        //c3.appendChild(lbl);
        //c3.appendChild(saveDashName);
        //c3.appendChild(saveBtn);

        let c4 = document.createElement("div");
        c4.classList.add("col-3");
        c4.appendChild(heading5Icon("cloud", "Load Layout"))
        let listRemote = document.createElement("div");
        listRemote.classList.add("dropdown");
        let listBtnRemote = document.createElement("button");
        listBtnRemote.type = "button";
        listBtnRemote.classList.add("btn", "btn-secondary", "dropdown-toggle", "btn-sm");
        listBtnRemote.setAttribute("data-bs-toggle", "dropdown");
        listBtnRemote.innerHTML = "<i class='fa fa-cloud'></i> Load Layout";
        listRemote.appendChild(listBtnRemote);
        let listUlRemote = document.createElement("ul");
        listUlRemote.classList.add("dropdown-menu");
        listUlRemote.id = "remoteDashletList";
        listRemote.appendChild(listUlRemote);
        c4.appendChild(listRemote);
        if (this.savedDashUrl.length > 0) {
            $.get(this.savedDashUrl, (data) => {
                let parent = document.getElementById("remoteDashletList");
                data.forEach((d) => {
                    let li = document.createElement("li");
                    let link = document.createElement("a");
                    link.innerText = d;
                    let filename = d;
                    link.onclick = () => {
                        console.log("Loading " + d);
                        $.ajax({
                            type: "POST",
                            url: "/local-api/dashletGet",
                            data: JSON.stringify({theme: filename}),
                            contentType: 'application/json',
                            success: (data) => {
                                this.dashletIdentities = data;
                                this.layout.save(this.dashletIdentities);
                                localStorage.setItem("forceEditMode", "true");
                                window.location.reload();
                            }
                        });
                    }
                    li.appendChild(link);
                    parent.appendChild(li);
                });
            });
        }

        row.appendChild(c1);
        row.appendChild(c2);
        if (this.savedDashUrl.length > 0) {
            row.appendChild(c3);
            row.appendChild(c4);
        }
        editDiv.appendChild(row);


        // Decorate all the dashboard elements with controls after a refresh period
        requestAnimationFrame(() => {
            setTimeout(() => { this.#updateEditDecorations() });
        });

        toasts.appendChild(editDiv);
    }

    #replaceDashletList() {
        // Apply
        this.build();
        let self = this;
        requestAnimationFrame(() => {
            setTimeout(() => { self.#updateEditDecorations() });
        });

        // Persist
        this.layout.save(this.dashletIdentities);
    }

    #updateEditDecorations() {
        let oldEditDiv = document.getElementById("divEditorElements");
        if (oldEditDiv !== null) editDiv.remove();

        let editDivParent = document.getElementById("dashboardEditingDiv");
        let editDiv = document.createElement("div");
        editDiv.id = "divEditorElements";

        for (let i=0; i<this.childIds.length; i++) {
            let dashDiv = document.getElementById(this.childIds[i]);
            if (dashDiv != null) {
                let clientRect = dashDiv.getBoundingClientRect();
                let clientLeft = (clientRect.left + 4) + "px";
                let clientRight = (clientRect.right - 34) + "px";
                let clientTop = (clientRect.top) + "px";
                let clientBottom = (clientRect.bottom) + "px";
                let clientMiddleY = (((clientRect.bottom - clientRect.top) / 2) + clientRect.top - 10) + "px";
                let clientMiddleX = (((clientRect.right - clientRect.left) / 2) + clientRect.left - 10) + "px";

                // Left Navigation Arrow
                if (i > 0) {
                    let myI = i;
                    editDiv.appendChild(this.#dashEditButton(
                        clientLeft,
                        clientMiddleY,
                        "arrow-circle-left",
                        "warning",
                        () => {
                            this.clickUp(myI);
                        }
                    ));
                }

                // Right Navigation Arrow
                if (i < this.childIds.length-1) {
                    let myI = i;
                    editDiv.appendChild(this.#dashEditButton(
                        clientRight,
                        clientMiddleY,
                        "arrow-circle-right",
                        "warning",
                        () => {
                            this.clickDown(myI);
                        }
                    ));
                }

                // Trash Button
                let myI = i;
                editDiv.appendChild(this.#dashEditButton(
                    clientMiddleX,
                    clientMiddleY,
                    "trash",
                    "danger",
                    () => {
                        this.clickTrash(myI);
                    }
                ));

                // Expand Button
                let myI2 = i;
                editDiv.appendChild(this.#dashEditButton(
                    clientLeft,
                    clientTop,
                    "plus-circle",
                    "secondary",
                    () => {
                        this.zoomIn(myI2);
                    }
                ));

                // Contract Button
                let myI3 = i;
                editDiv.appendChild(this.#dashEditButton(
                    (clientRect.left + 40) + "px",
                    clientTop,
                    "minus-circle",
                    "secondary",
                    () => {
                        this.zoomOut(myI3);
                    }
                ));
            } else {
                console.log("Warning: NULL div found in dashlet list");
            }
        }

        editDivParent.appendChild(editDiv);
    }

    #dashEditButton(left, top, iconSuffix, style, closure) {
        let div = document.createElement("div");
        div.style.position = "absolute";
        div.style.width = "20px";
        div.style.zIndex = "200";
        div.style.height = "20px";
        div.style.top = top;
        div.style.left = left;
        div.classList.add("dashEditButton");
        let button = document.createElement("button");
        button.type = "button";
        button.classList.add("btn", "btn-sm", "btn-" + style);
        button.innerHTML = "<i class='fa fa-" + iconSuffix +"'></i>";
        button.onclick = closure;
        div.appendChild(button);
        return div;
    }

    closeEditMode() {
        let editor = document.getElementById("dashboardEditingDiv");
        if (editor != null) {
            editor.remove();
        }
        this.editingDashboard = false;
    }

    clickUp(i) {
        let toMove = this.dashletIdentities[i];
        let toReplace = this.dashletIdentities[i-1];
        this.dashletIdentities[i-1] = toMove;
        this.dashletIdentities[i] = toReplace;

        this.#replaceDashletList();
    }

    clickDown(i) {
        let toMove = this.dashletIdentities[i];
        let toReplace = this.dashletIdentities[i+1];
        this.dashletIdentities[i+1] = toMove;
        this.dashletIdentities[i] = toReplace;

        this.#replaceDashletList();
    }

    clickTrash(i) {
        this.dashletIdentities.splice(i, 1);
        this.layout.save(this.dashletIdentities);
        localStorage.setItem("forceEditMode", "true");
        window.location.reload();
    }

    zoomIn(i) {
        console.log(i);
        if (this.dashletIdentities[i].size < 12) {
            this.dashletIdentities[i].size += 1;
        }
        this.layout.save(this.dashletIdentities);
        localStorage.setItem("forceEditMode", "true");
        window.location.reload();
    }

    zoomOut(i) {
        if (this.dashletIdentities[i].size > 1) {
            this.dashletIdentities[i].size -= 1;
        }
        this.layout.save(this.dashletIdentities);
        localStorage.setItem("forceEditMode", "true");
        window.location.reload();
    }

    removeAll() {
        this.dashletIdentities = [];
        this.layout.save(this.dashletIdentities);
        localStorage.setItem("forceEditMode", "true");
        window.location.reload();
    }

    addAll() {
        this.dashletIdentities = DashletMenu;
        this.layout.save(this.dashletIdentities);
        localStorage.setItem("forceEditMode", "true");
        window.location.reload();
    }
}
